import React, { useEffect, useRef } from "react";
import {
  AnchorLink,
  Button,
  Container,
  Layout,
  Modal,
  Padding,
  Select,
  Text,
  TextArea,
} from "@kidslivesafe/sc-react";
import {
  selectMemberExperienceData,
  setMemberExperienceDataAsync,
} from "../../../features/authentication/authenticationSlice";
import { selectBrandInfo } from "../../../features/brand/brandSlice";
import {
  selectAllReportHistory,
  selectPreSiteJabberModalShown,
  setPreSiteJabberModalShown,
  setShowSiteJabberFromFeedback,
} from "../../../features/reports/reportsSlice";
import { useAppDispatch, useAppSelector } from "../../../hooks/hooks";
import { sendMemberEventAsync } from "../../../features/admin/adminSlice";
import { formatPhoneNumber } from "../../../utils";

export interface SiteJabberModalProps {}

const SiteJabberModal: React.FC<SiteJabberModalProps> = () => {
  const [show, setShow] = React.useState(true);
  const dispatch = useAppDispatch();
  const memberExperienceData = useAppSelector(selectMemberExperienceData);
  const preSiteJabberModalShown = useAppSelector(selectPreSiteJabberModalShown);
  const showSiteJabberModal = useRef(false);
  const brandInfo = useAppSelector(selectBrandInfo);
  const allReportHistory = useAppSelector(selectAllReportHistory);
  const [currentScreen, setCurrentScreen] = React.useState<
    "initial" | "feedback" | "thank-you"
  >("initial");
  const [selectedReason, setSelectedReason] = React.useState("");
  const [selectedReasonError, setSelectedReasonError] = React.useState(false);
  const [feedbackText, setFeedbackText] = React.useState<string>("");
  const [feedbackTextError, setFeedbackTextError] =
    React.useState<boolean>(false);

  const handleExternalNavigation = (route: string) => {
    window.open(
      `${brandInfo.homeUrl}/help-center/${route}`,
      "_blank",
      "noopener",
    );
  };

  useEffect(() => {
    let showModalFromReportHistory = false;
    let showModalFromMemberExperience = true;
    if (allReportHistory) {
      const filteredArray = allReportHistory.filter(report => {
        if (report.type === "people") {
          if (report.origin !== "Funnel") {
            return report;
          } else {
            return false;
          }
        } else {
          return report;
        }
      });
      if (filteredArray.length > 2) {
        showModalFromReportHistory = true;
      }

      if (
        memberExperienceData &&
        memberExperienceData["shownPreSiteJabberModal"] === "true"
      ) {
        showModalFromMemberExperience = false;
      }
      showSiteJabberModal.current =
        showModalFromReportHistory &&
        showModalFromMemberExperience &&
        !preSiteJabberModalShown;
    }
  }, [allReportHistory, preSiteJabberModalShown, memberExperienceData]);

  useEffect(() => {
    if (showSiteJabberModal.current) {
      dispatch(setPreSiteJabberModalShown());
      dispatch(
        setMemberExperienceDataAsync({
          key: "shownPreSiteJabberModal",
          value: true,
        }),
      );
    }
  }, [dispatch, showSiteJabberModal]);

  // add the ! back
  if (!showSiteJabberModal.current) {
    return null;
  } else {
    return (
      <Modal
        open={show}
        setOpen={() => setShow(false)}
        size={currentScreen === "initial" ? "base" : "fit-content"}
        outSideClose={currentScreen === "feedback" ? false : true}
        xClose={currentScreen === "feedback" ? false : true}
        centerContentVertically
        scrollable={false}
        fillHorizontallyOnMobile
      >
        {currentScreen === "initial" ? (
          <Layout
            type="flex"
            fillVertical
            flexProps={{
              gap: "xl",
              direction: "column",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Layout
              type="flex"
              flexProps={{
                gap: "lg",
                direction: "column",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Text tag="h2" textAlign="center">
                We value your feedback!
              </Text>

              <Text textAlign="center" fontWeight="normal">
                How has your experience with {brandInfo.name} been so far?
              </Text>
            </Layout>

            <Layout
              type="flex"
              flexProps={{
                gap: "md",
                justifyContent: "center",
                alignItems: "center",
                direction: "column",
              }}
              fillHorizontal
            >
              <Button
                handleClick={() => {
                  setShow(false);
                  dispatch(setShowSiteJabberFromFeedback());
                  dispatch(
                    setMemberExperienceDataAsync({
                      key: "showSiteJabberFromFeedback",
                      value: true,
                    }),
                  );
                  dispatch(
                    sendMemberEventAsync({
                      EventCategory: "UserExperienceRequest",
                      EventAction: "I Love It!",
                    }),
                  );
                }}
                buttonText="I Love It!"
                size="md"
                fill="default"
                color="primary"
              />
              <Button
                handleClick={() => {
                  setCurrentScreen("feedback");
                  dispatch(
                    sendMemberEventAsync({
                      EventCategory: "UserExperienceRequest",
                      EventAction: "It Could Be Better",
                    }),
                  );
                }}
                buttonText="It Could Be Better..."
                size="md"
                fill="default"
                color="secondary"
              />
            </Layout>
          </Layout>
        ) : currentScreen === "feedback" ? (
          // feedback screen
          <Layout
            type="flex"
            fillVertical
            flexProps={{
              gap: "lg",
              direction: "column",
              alignItems: "flex-start",
              justifyContent: "center",
            }}
          >
            <Layout
              type="flex"
              flexProps={{
                gap: "md",
                justifyContent: "center",
                alignItems: "flex-start",
                direction: "column",
              }}
            >
              <Text fontWeight="bold">We'd love to hear from you!</Text>
              <Layout
                type="flex"
                flexProps={{
                  gap: "lg",
                  justifyContent: "center",
                  alignItems: "flex-start",
                  direction: "column",
                }}
              >
                <Text fontWeight="normal">
                  Your feedback helps us to improve and is reviewed by real
                  people to shape future product updates.
                </Text>
                <Text fontWeight="normal">
                  What area would be the most helpful for us to focus on?
                </Text>
              </Layout>
            </Layout>
            <Layout
              type="flex"
              fillHorizontal
              flexProps={{
                gap: "md",
                justifyContent: "center",
                alignItems: "center",
                direction: "column",
              }}
            >
              <Select
                onOptionSelected={setSelectedReason}
                label="Select a Category"
                error={selectedReasonError}
                handleError={setSelectedReasonError}
                options={[
                  {
                    label: "Search Accuracy",
                    value: "Search Accuracy",
                  },
                  {
                    label: "Data Completeness",
                    value: "Data Completeness",
                  },
                  {
                    label: "Website Performance",
                    value: "Website Performance",
                  },
                  {
                    label: "Website Navigation",
                    value: "Website Navigation",
                  },
                  {
                    label: "Pricing & Plans",
                    value: "Pricing & Plans",
                  },
                  {
                    label: "Customer Support",
                    value: "Customer Support",
                  },
                  {
                    label: "Billing Issues",
                    value: "Billing Issues",
                  },
                  {
                    label: "Something Else",
                    value: "Something Else",
                  },
                ]}
                value={selectedReason}
              />
              <Layout
                type="flex"
                fillHorizontal
                flexProps={{ gap: "xs", direction: "column" }}
              >
                <Text>Anything else?</Text>
                <TextArea
                  id="feedback-textarea"
                  placeholder="Enter at least 5 characters to submit feedback"
                  handleChange={input => {
                    if (input.length > 500) {
                      setFeedbackTextError(true);
                    } else {
                      setFeedbackTextError(false);
                    }
                    setFeedbackText(input);
                  }}
                  value={feedbackText}
                  characterLimit={500}
                  error={feedbackTextError}
                  errorText="Exceeds character maximum of 500"
                />
                <Text size="xs" tag="i">
                  Please do not enter personal information into this form
                </Text>
              </Layout>
            </Layout>
            <Layout
              type="flex"
              flexProps={{
                gap: "xxs",
                justifyContent: "center",
                alignItems: "center",
                direction: "column-reverse",
              }}
              breakpointStyles={{
                desktop: {
                  type: "flex",
                  flexProps: {
                    gap: "md",
                    justifyContent: "center",
                    alignItems: "center",
                    direction: "row",
                  },
                },
              }}
              fillHorizontal
            >
              <Padding space="md">
                <AnchorLink
                  role="button"
                  hoverUnderline
                  handleClick={() => {
                    setCurrentScreen("thank-you");
                  }}
                >
                  No Thank You
                </AnchorLink>
              </Padding>
              <Button
                handleClick={() => {
                  if (feedbackText.length > 500) {
                    setFeedbackTextError(true);
                  } else {
                    dispatch(
                      sendMemberEventAsync({
                        EventCategory: "Click",
                        EventAction: "Submit Feedback",
                        MetaData: JSON.stringify({
                          ["Feedback Reason"]:
                            selectedReason.length > 0
                              ? selectedReason
                              : "No Feedback Reason Provided",
                          ["Feedback Text"]:
                            feedbackText.length > 0
                              ? feedbackText
                              : "No Feedback Text Provided",
                        }),
                      }),
                    );
                    setCurrentScreen("thank-you");
                  }
                }}
                buttonText="Submit Feedback"
                size="md"
                disabled={
                  !(selectedReason.length > 0 || feedbackText.length > 5)
                }
                fill="default"
                color="primary"
              />
            </Layout>
          </Layout>
        ) : (
          // thank you screen
          <Layout
            type="flex"
            fillVertical
            flexProps={{
              gap: "lg",
              direction: "column",
              alignItems: "flex-start",
              justifyContent: "center",
            }}
          >
            <Layout
              type="flex"
              flexProps={{
                gap: "md",
                justifyContent: "center",
                alignItems: "flex-start",
                direction: "column",
              }}
            >
              <Text fontWeight="bold">Thank you for your feedback!</Text>

              <Text fontWeight="normal">
                We appreciate you taking the time to help us improve our
                service.
              </Text>
            </Layout>
            <Container altColor="grey">
              <Padding space="md">
                <Layout
                  type="flex"
                  flexProps={{
                    gap: "md",
                    justifyContent: "center",
                    alignItems: "flex-start",
                    direction: "column",
                  }}
                >
                  <Text fontWeight="bold">Need more help?</Text>
                  <Text>
                    Check out our{" "}
                    <AnchorLink
                      inline
                      role="button"
                      handleClick={() => handleExternalNavigation("faqs")}
                    >
                      FAQs
                    </AnchorLink>
                    ,{" "}
                    <AnchorLink
                      inline
                      role="button"
                      handleClick={() =>
                        handleExternalNavigation("help-center")
                      }
                    >
                      Help Center
                    </AnchorLink>
                    , or contact us for any additional information.
                  </Text>
                  <Text fontWeight="bold">Contact us:</Text>
                  <Layout
                    type="flex"
                    flexProps={{
                      gap: "xxs",
                      justifyContent: "center",
                      alignItems: "flex-start",
                      direction: "column",
                    }}
                  >
                    <Text>
                      Email:{" "}
                      <AnchorLink href={brandInfo.email} email inline>
                        {brandInfo.email}
                      </AnchorLink>
                    </Text>
                    <Text>
                      Phone:{" "}
                      <AnchorLink
                        href={brandInfo.phoneNumber}
                        phoneNumber
                        inline
                      >
                        {formatPhoneNumber(brandInfo.phoneNumber)}
                      </AnchorLink>
                    </Text>
                    <Text size="sm" tag="i">
                      Our team members are happy to assist you.
                    </Text>
                  </Layout>
                </Layout>
              </Padding>
            </Container>
            <Layout
              type="flex"
              flexProps={{
                gap: "xxs",
                justifyContent: "center",
                alignItems: "flex-end",
                direction: "column-reverse",
              }}
              fillHorizontal
            >
              <Button
                handleClick={() => {
                  setCurrentScreen("initial");
                  setShow(false);
                }}
                buttonText="Close"
                size="md"
                fill="default"
                color="primary"
              />
            </Layout>
          </Layout>
        )}
      </Modal>
    );
  }
};

export default SiteJabberModal;
